/**
 * Pure helpers for per-agent model specs (#1490).
 *
 * Shared by the two admin model-picker surfaces — the global
 * `Agents → Assignments` page and the per-plan editor's Agent models
 * section — so the parse/compose/price-status logic lives in exactly
 * one place and can't drift between the two. All pure functions, no
 * React, no I/O.
 */
import type { AgentModelOverride, ModelPricingRow } from './api'

/** Compose a `<provider>:<model>` namePattern from an AgentModelOverride
 * (the doc shape) — falls back to bare model when provider is unset.
 * Returns `''` when neither is set so a combined dropdown collapses to
 * the "cascade / inherit" sentinel. */
export function overrideToNamePattern(o: AgentModelOverride | undefined): string {
  if (!o?.model) return ''
  return o.provider ? `${o.provider}:${o.model}` : o.model
}

/** Split a namePattern on its FIRST colon — `openai:gpt-5.4-mini` →
 * `{ provider: 'openai', model: 'gpt-5.4-mini' }`. Model ids
 * occasionally contain `:` themselves; splitting on the first colon
 * preserves the rest as the model id. A pattern with no colon is
 * treated as model-only (provider undefined) — the resolver tolerates
 * that and a few pricing-catalog rows use this shape. */
export function parseNamePattern(pattern: string): { provider?: string; model: string } {
  const i = pattern.indexOf(':')
  if (i < 0) return { model: pattern }
  return {
    provider: pattern.slice(0, i),
    model: pattern.slice(i + 1),
  }
}

/** Whether the catalog has a real (non-placeholder) price for this
 * model spec. `_unpriced: true` is the discovery-placeholder signal — a
 * row exists but rates aren't filled in, so the cost rollup contributes
 * zero. A missing row counts as unpriced too. An empty pattern (the
 * "inherit / cascade" sentinel) returns `'unknown'` — the resolver will
 * use a lower tier and we can't yet say which model that is; the caller
 * suppresses the badge for that case. */
export function modelPriceStatus(
  namePattern: string,
  pricingRows: readonly ModelPricingRow[],
): 'priced' | 'unpriced' | 'unknown' {
  if (!namePattern.trim()) return 'unknown'
  const row = pricingRows.find((r) => r.namePattern === namePattern)
  if (!row) return 'unpriced'
  return row._unpriced === true ? 'unpriced' : 'priced'
}

/** Whether the catalog says this model can read image input (#1844 Slice 1).
 * Reads the `imageInput` capability flag (from #1842) off the matched
 * pricing row. A missing row, empty pattern, or unset flag → false — we
 * only badge a model as image-capable when the catalog affirmatively says
 * so (fail-safe, matching the backend resolver's default). Purely
 * informational: it drives the "vision enabled" badge and never gates which
 * model an operator may pick. */
export function modelReadsImages(
  namePattern: string,
  pricingRows: readonly ModelPricingRow[],
): boolean {
  if (!namePattern.trim()) return false
  const row = pricingRows.find((r) => r.namePattern === namePattern)
  return row?.imageInput === true
}

/** The matched model's relative cost category (#1844 Slice 2), or undefined
 * (unpriced / unknown). Read off the pricing row's server-derived
 * `costCategory`. Purely informational — the picker shows it as a pill and
 * never gates a choice. */
export function modelCostCategory(
  namePattern: string,
  pricingRows: readonly ModelPricingRow[],
): ModelPricingRow['costCategory'] {
  if (!namePattern.trim()) return undefined
  return pricingRows.find((r) => r.namePattern === namePattern)?.costCategory
}

/** #1844 Slice 1.5 — facet keys the model picker filters on. The editorial
 * keys (`otto-curated`, `frontier`) are the wire contract emitted by the
 * server's `curatedModels.ts` onto `ModelPricingRow.collections`; there is
 * no shared server↔frontend package, so these literals are mirrored by hand
 * (same as `ModelTargetRow`). `reads-images` is derived from `imageInput`. */
export const FACET_CURATED = 'otto-curated'
export const FACET_FRONTIER = 'frontier'
export const FACET_READS_IMAGES = 'reads-images'
/** #1928 — models the catalog CONFIRMS meet the requirements of the agent whose
 * row is being edited. Unlike the editorial facets, membership is agent-
 * dependent, so it cannot be a static `collections` entry: it is computed per
 * render from the served requirement map plus the row's capability values.
 *
 * The key deliberately reads "known compatible", not "compatible": the facet
 * asserts EVIDENCE, and a model the catalog is silent about is excluded from it
 * while remaining perfectly usable under All Models and via search. */
export const FACET_WORKS_WITH_AGENT = 'works-with-agent'

/** The facet keys an option carries, derived from its pricing row: the
 * editorial `collections` (curated / frontier) plus the derived
 * `reads-images` capability. Pass the already-resolved row — a caller
 * building a whole option list should look each row up once via a map, not
 * re-`find` per option. Returns `[]` for a missing / unpriced row, which
 * leaves the option out of every facet but still visible under "All Models"
 * and search. */
export function optionFacets(
  row: Pick<ModelPricingRow, 'imageInput' | 'collections' | 'toolCalling'> | undefined,
  /** #1928 — capabilities the agent being edited requires. Pass `[]` (or omit)
   * for a picker with no agent context; the agent facet is then never applied.
   * Served by the backend, never mirrored here: this rule decides whether a run
   * may spend money, and a hand-copied second copy is the drift #1902 spent two
   * slices removing from the agent prompts. */
  requiredCapabilities: readonly string[] = [],
): string[] {
  if (!row) return []
  const facets: string[] = row.collections ? [...row.collections] : []
  if (row.imageInput === true) facets.push(FACET_READS_IMAGES)
  // Membership requires a POSITIVE `true` on every requirement. `undefined`
  // (catalog silent) is excluded on purpose — the facet claims evidence, and
  // including unknowns would overstate what we know. `false` is excluded
  // because the model genuinely cannot.
  const meetsEveryRequirement = requiredCapabilities.every(cap => {
    // Tool calling is the only capability this facet can evaluate. An
    // unrecognised requirement excludes the model rather than being ignored:
    // the facet claims evidence, and "we do not know how to check this" is not
    // evidence. Conservative in the safe direction — the model stays reachable
    // under All Models and by search.
    if (cap !== 'toolCalling') return false
    return row.toolCalling === true
  })
  if (requiredCapabilities.length > 0 && meetsEveryRequirement) {
    facets.push(FACET_WORKS_WITH_AGENT)
  }
  return facets
}
